// Superchat Spec 01 — the on-air read-aloud graphic's feed + control.
//
// One featured superchat at a time. The host/cohost publishes feature/clear
// control messages on FEATURE_TOPIC; every participant subscribes and mirrors
// the current featured superchat into `featured` (drives the overlay).
//
// SECURITY: the receive-side origin gate is the PUBLISHER gate (canPublish),
// exactly the useCohostChat pattern — hosts and cohosts can feature, viewers
// never can (no viewer token carries canPublish). permissions can be transiently
// undefined right after join, so we FAIL CLOSED: undefined = not a publisher.
//
// A single shared instance backs both the host queue (which calls
// featureSuperchat/clearFeatured) and the overlay (which reads featured) via
// FeaturedSuperchatProvider — LiveKit never loops your own data back, so the
// publisher relies on the local echo the hook applies on send.

import {
  FEATURE_TOPIC,
  decodeFeatureControl,
  encodeFeatureControl,
  type FeatureControlMessage,
  type SuperchatMessage,
} from '@archetelos/lib/live';
import { useRoomContext } from '@livekit/components-react';
import { RoomEvent } from 'livekit-client';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import type { Participant, Room } from 'livekit-client';
import type { ReactNode } from 'react';

/** Late-joiner convergence: re-publish the active feature state on this cadence
 *  so a viewer who joins mid-feature sees it within one interval. */
const REPUBLISH_MS = 10_000;

/** Publisher gate — fail closed: undefined permissions = not a publisher. */
function isPublisher(p: Participant | undefined): boolean {
  return p?.permissions?.canPublish === true;
}

export interface UseFeaturedSuperchatResult {
  /** The superchat currently on-air, or null. */
  featured: SuperchatMessage | null;
  /** Put a superchat on-air (replaces any current one). Publisher-only. */
  featureSuperchat: (sc: SuperchatMessage) => void;
  /** Dismiss the on-air superchat. Publisher-only. */
  clearFeatured: () => void;
}

export function useFeaturedSuperchat(room: Room | null): UseFeaturedSuperchatResult {
  const [featured, setFeatured] = useState<SuperchatMessage | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Receive: topic → origin (publisher) → shape, then mirror state. A repeated
  // id is a refresh, not a change — keep the same reference to avoid churn.
  useEffect(() => {
    if (!room) return;
    const onData = (
      payload: Uint8Array,
      participant?: Participant,
      _kind?: unknown,
      topic?: string,
    ): void => {
      if (topic !== FEATURE_TOPIC) return;
      if (!isPublisher(participant)) return;
      const msg = decodeFeatureControl(payload);
      if (!msg) return;
      setFeatured((prev) => {
        if (msg.kind === 'feature-clear') return null;
        if (prev && prev.id === msg.superchat.id) return prev; // refresh, no change
        return msg.superchat;
      });
    };
    room.on(RoomEvent.DataReceived, onData);
    return () => {
      room.off(RoomEvent.DataReceived, onData);
    };
  }, [room]);

  const stopRepublish = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const publishControl = useCallback((r: Room, msg: FeatureControlMessage) => {
    void r.localParticipant.publishData(encodeFeatureControl(msg), {
      topic: FEATURE_TOPIC,
      reliable: true,
    });
  }, []);

  const featureSuperchat = useCallback(
    (sc: SuperchatMessage) => {
      if (!room) return;
      setFeatured(sc); // local echo — LiveKit doesn't loop our own data back
      const send = (): void =>
        publishControl(room, { v: 1, kind: 'feature-superchat', superchat: sc, ts: Date.now() });
      send();
      stopRepublish();
      intervalRef.current = setInterval(send, REPUBLISH_MS);
    },
    [room, publishControl, stopRepublish],
  );

  const clearFeatured = useCallback(() => {
    stopRepublish();
    setFeatured(null);
    if (room) publishControl(room, { v: 1, kind: 'feature-clear', ts: Date.now() });
  }, [room, publishControl, stopRepublish]);

  // Stop the re-publish interval on unmount.
  useEffect(() => stopRepublish, [stopRepublish]);

  return { featured, featureSuperchat, clearFeatured };
}

// --- Shared instance (provider/context) ----------------------------------

const NOOP: UseFeaturedSuperchatResult = {
  featured: null,
  featureSuperchat: () => {},
  clearFeatured: () => {},
};

const FeaturedSuperchatContext = createContext<UseFeaturedSuperchatResult | null>(null);

/**
 * Wraps a subtree inside a LiveKitRoom so the host queue and the on-air overlay
 * share ONE hook instance (the publisher's local echo reaches the overlay).
 */
export function FeaturedSuperchatProvider({ children }: { children: ReactNode }) {
  const room = useRoomContext();
  const value = useFeaturedSuperchat(room);
  return (
    <FeaturedSuperchatContext.Provider value={value}>{children}</FeaturedSuperchatContext.Provider>
  );
}

/** Consume the shared featured-superchat state. Falls back to a no-op result
 *  when rendered outside a provider (e.g. an isolated preview) — never throws. */
export function useFeaturedSuperchatContext(): UseFeaturedSuperchatResult {
  return useContext(FeaturedSuperchatContext) ?? NOOP;
}
